import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    Users,
    Search,
    MoreVertical,
    Eye,
    Edit,
    Shield,
    Calendar,
    ArrowLeft,
    Plus,
    Loader2,
    AlertCircle,
    CheckCircle,
    XCircle,
    Clock,
    Filter,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { merchantService } from '@/services/merchantService'
import { MerchantUser } from '@/types'
import { safeFormatDistanceToNow } from '@/utils/date'
import { useAuthStore } from '@/stores/authStore'
import { useAuth } from '@/hooks/useAuth'
import { getVisibleRoles, canManageMerchantUsers } from '@/utils/auth'
import { useAuditLog } from '@/utils/auditLogger'

export default function MerchantUsersList() {
    const navigate = useNavigate()
    const { user } = useAuthStore()
    const { user: authUser } = useAuth()
    const { logUserAction, logDataAccess } = useAuditLog()
    const [users, setUsers] = useState<MerchantUser[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [searchTerm, setSearchTerm] = useState('')
    const [roleFilter, setRoleFilter] = useState<string>('')
    const [statusFilter, setStatusFilter] = useState<string>('')

    useEffect(() => {
        if (user?.merchant) {
            fetchUsers()
        } else {
            setError('No merchant associated with this user')
            setLoading(false)
        }
    }, [user])

    // Filter users based on current user's permissions
    const filterUsersByPermissions = (users: MerchantUser[]): MerchantUser[] => {
        if (!authUser) return []

        // Platform admins can see all users
        if (canManageMerchantUsers(authUser)) {
            return users
        }

        // Merchant admins can see all users from their merchant
        if (authUser.role === 'merchant_admin' && authUser.merchant) {
            return users.filter(u =>
                u.role === 'merchant_admin' ||
                u.role === 'merchant_user'
            )
        }

        // Other users can't see any users
        return []
    }

    const fetchUsers = async () => {
        if (!user?.merchant) {
            setError('No merchant ID found for this user')
            setLoading(false)
            return
        }

        try {
            setLoading(true)
            setError(null)

            const response = await merchantService.getMyMerchantUsers(user.merchant)

            if (response.success) {
                const filteredUsers = filterUsersByPermissions(response.data)

                // Log data access
                logDataAccess(authUser, 'merchant_user_list', user.merchant, true, 'Successfully fetched merchant users', {
                    totalUsers: response.data.length,
                    filteredUsers: filteredUsers.length,
                    merchantId: user.merchant
                })

                setUsers(filteredUsers)
            } else {
                // Log failed data access
                logDataAccess(authUser, 'merchant_user_list', user.merchant, false, 'Failed to fetch merchant users')
                setError('Failed to fetch merchant users')
            }
        } catch (err) {
            setError('An error occurred while fetching merchant users')
            console.error('Error fetching merchant users:', err)
        } finally {
            setLoading(false)
        }
    }

    const handleSearch = (value: string) => {
        setSearchTerm(value)
    }

    const handleRoleFilter = (role: string) => {
        setRoleFilter(role)
    }

    const handleStatusFilter = (status: string) => {
        setStatusFilter(status)
    }

    const clearFilters = () => {
        setSearchTerm('')
        setRoleFilter('')
        setStatusFilter('')
    }

    const getRoleColor = (role: string) => {
        switch (role) {
            case 'merchant_admin':
                return 'text-purple-600 bg-purple-50 border-purple-200'
            case 'merchant_user':
                return 'text-blue-600 bg-blue-50 border-blue-200'
            default:
                return 'text-gray-600 bg-gray-50 border-gray-200'
        }
    }

    const getRoleIcon = (role: string) => {
        switch (role) {
            case 'merchant_admin':
                return Shield
            case 'merchant_user':
                return Users
            default:
                return Users
        }
    }

    const getStatusColor = (isActive: boolean) => {
        return isActive
            ? 'text-green-600 bg-green-50 border-green-200'
            : 'text-red-600 bg-red-50 border-red-200'
    }

    const getStatusIcon = (isActive: boolean) => {
        return isActive ? CheckCircle : XCircle
    }

    // Filter users based on search and filters
    const filteredUsers = users.filter(user => {
        const matchesSearch = !searchTerm ||
            user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.last_name.toLowerCase().includes(searchTerm.toLowerCase())

        const matchesRole = !roleFilter || user.role === roleFilter
        const matchesStatus = !statusFilter ||
            (statusFilter === 'active' && user.is_active) ||
            (statusFilter === 'inactive' && !user.is_active)

        return matchesSearch && matchesRole && matchesStatus
    })

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="flex items-center space-x-2">
                    <Loader2 className="h-6 w-6 animate-spin" />
                    <span>Loading merchant users...</span>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => navigate('/dashboard')}
                                className="hover:bg-gray-100"
                            >
                                <ArrowLeft className="h-5 w-5" />
                            </Button>
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900">Merchant Users</h1>
                                <p className="text-gray-600 mt-1">Manage users for your merchant account</p>
                            </div>
                        </div>
                        {canManageMerchantUsers(authUser) && (
                            <Button
                                onClick={() => {
                                    logUserAction(authUser, 'create_merchant_user_form', 'merchant_user', undefined, {
                                        action: 'navigate_to_create_form',
                                        merchantId: user?.merchant
                                    })
                                    navigate('/merchant/users/create')
                                }}
                                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white"
                            >
                                <Plus className="h-4 w-4 mr-2" />
                                Add User
                            </Button>
                        )}
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    <div className="bg-white rounded-lg shadow-sm p-6">
                        <div className="flex items-center">
                            <div className="p-2 bg-blue-100 rounded-lg">
                                <Users className="h-6 w-6 text-blue-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Total Users</p>
                                <p className="text-2xl font-bold text-gray-900">{users.length}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm p-6">
                        <div className="flex items-center">
                            <div className="p-2 bg-purple-100 rounded-lg">
                                <Shield className="h-6 w-6 text-purple-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Admins</p>
                                <p className="text-2xl font-bold text-gray-900">
                                    {users.filter(u => u.role === 'merchant_admin').length}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm p-6">
                        <div className="flex items-center">
                            <div className="p-2 bg-green-100 rounded-lg">
                                <CheckCircle className="h-6 w-6 text-green-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Active</p>
                                <p className="text-2xl font-bold text-gray-900">
                                    {users.filter(u => u.is_active).length}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm p-6">
                        <div className="flex items-center">
                            <div className="p-2 bg-yellow-100 rounded-lg">
                                <Clock className="h-6 w-6 text-yellow-600" />
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Verified</p>
                                <p className="text-2xl font-bold text-gray-900">
                                    {users.filter(u => u.is_verified).length}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="flex-1">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                                <Input
                                    placeholder="Search users by name or email..."
                                    value={searchTerm}
                                    onChange={(e) => handleSearch(e.target.value)}
                                    className="pl-10"
                                />
                            </div>
                        </div>

                        <div className="flex gap-2">
                            <select
                                value={roleFilter}
                                onChange={(e) => handleRoleFilter(e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                            >
                                <option value="">All Roles</option>
                                {getVisibleRoles(authUser).map(role => (
                                    <option key={role} value={role}>
                                        {role === 'merchant_admin' ? 'Admin' :
                                            role === 'merchant_user' ? 'User' : role}
                                    </option>
                                ))}
                            </select>

                            <select
                                value={statusFilter}
                                onChange={(e) => handleStatusFilter(e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                            >
                                <option value="">All Status</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>

                            {(searchTerm || roleFilter || statusFilter) && (
                                <Button
                                    variant="outline"
                                    onClick={clearFilters}
                                    className="text-gray-600"
                                >
                                    <Filter className="h-4 w-4 mr-2" />
                                    Clear
                                </Button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Error State */}
                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                        <div className="flex items-center">
                            <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
                            <span className="text-red-800">{error}</span>
                        </div>
                    </div>
                )}

                {/* Users Table */}
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        User
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Role
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Verification
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Last Login
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Created
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredUsers.map((user) => {
                                    const RoleIcon = getRoleIcon(user.role)
                                    const StatusIcon = getStatusIcon(user.is_active)
                                    return (
                                        <tr key={user.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div className="flex-shrink-0 h-10 w-10">
                                                        <div className="h-10 w-10 rounded-full bg-gradient-to-r from-orange-500 to-orange-600 flex items-center justify-center">
                                                            <Users className="h-5 w-5 text-white" />
                                                        </div>
                                                    </div>
                                                    <div className="ml-4">
                                                        <div className="text-sm font-medium text-gray-900">
                                                            {user.first_name} {user.last_name}
                                                        </div>
                                                        <div className="text-sm text-gray-500">{user.email}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getRoleColor(user.role)}`}>
                                                    <RoleIcon className="h-3 w-3 mr-1" />
                                                    {user.role === 'merchant_admin' ? 'Admin' : 'User'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(user.is_active)}`}>
                                                    <StatusIcon className="h-3 w-3 mr-1" />
                                                    {user.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${user.is_verified
                                                    ? 'text-green-600 bg-green-50 border-green-200'
                                                    : 'text-yellow-600 bg-yellow-50 border-yellow-200'
                                                    }`}>
                                                    {user.is_verified ? (
                                                        <>
                                                            <CheckCircle className="h-3 w-3 mr-1" />
                                                            Verified
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Clock className="h-3 w-3 mr-1" />
                                                            Pending
                                                        </>
                                                    )}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                <div className="flex items-center">
                                                    <Calendar className="h-4 w-4 text-gray-400 mr-1" />
                                                    {safeFormatDistanceToNow(user.last_login, 'Never', { addSuffix: true })}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                <div className="flex items-center">
                                                    <Calendar className="h-4 w-4 text-gray-400 mr-1" />
                                                    {safeFormatDistanceToNow(user.created_at, 'Unknown', { addSuffix: true })}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex items-center justify-end space-x-2">
                                                    {/* View button - visible to all authenticated users */}
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => {
                                                            logUserAction(authUser, 'view_merchant_user', 'merchant_user', user.id, {
                                                                userEmail: user.email,
                                                                userRole: user.role
                                                            })
                                                            navigate(`/merchant/users/${user.id}`)
                                                        }}
                                                        className="text-gray-600 hover:text-gray-900"
                                                    >
                                                        <Eye className="h-4 w-4" />
                                                    </Button>

                                                    {/* Edit button - only visible to users who can manage merchant users */}
                                                    {canManageMerchantUsers(authUser) && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => {
                                                                logUserAction(authUser, 'edit_merchant_user', 'merchant_user', user.id, {
                                                                    userEmail: user.email,
                                                                    userRole: user.role
                                                                })
                                                                navigate(`/merchant/users/${user.id}/edit`)
                                                            }}
                                                            className="text-gray-600 hover:text-gray-900"
                                                        >
                                                            <Edit className="h-4 w-4" />
                                                        </Button>
                                                    )}

                                                    {/* More actions - only visible to users who can manage merchant users */}
                                                    {canManageMerchantUsers(authUser) && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="text-gray-600 hover:text-gray-900"
                                                        >
                                                            <MoreVertical className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Empty State */}
                    {filteredUsers.length === 0 && !loading && (
                        <div className="text-center py-12">
                            <Users className="mx-auto h-12 w-12 text-gray-400" />
                            <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
                            <p className="mt-1 text-sm text-gray-500">
                                {searchTerm || roleFilter || statusFilter
                                    ? 'Try adjusting your search criteria.'
                                    : 'Get started by adding a user to your merchant account.'}
                            </p>
                            {canManageMerchantUsers(authUser) && (
                                <div className="mt-6">
                                    <Button
                                        onClick={() => {
                                            logUserAction(authUser, 'create_merchant_user_form', 'merchant_user', undefined, {
                                                action: 'navigate_to_create_form',
                                                merchantId: user?.merchant
                                            })
                                            navigate('/merchant/users/create')
                                        }}
                                        className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white"
                                    >
                                        <Plus className="h-4 w-4 mr-2" />
                                        Add User
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
